@if (isLoading) {
  <div class="spinner-overlay">
    <mat-spinner></mat-spinner>
  </div>
}

<h1 class="title">{{ config.title }}</h1>
<mat-card class="simulator-card">
  <mat-card-content class="simulator-content">
    <div class="simulator-layout">
      <mat-expansion-panel [expanded]="true" class="pc-results">
        <mat-expansion-panel-header>
          <mat-panel-title>Select Capillary pressure simulation input</mat-panel-title>
        </mat-expansion-panel-header>

        <div class="search">
          <mat-form-field appearance="outline" class="search-by-input">
            <mat-label>Search By</mat-label>
            <mat-select
              [(ngModel)]="filterByOptionSelected"
              id="search-by-filter-select"
              >
              @for (option of searchByOptions; track option.value) {
                <mat-option [value]="option.value"> {{ option.label }} </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="search-input-wrapper">
            <mat-label>Simulation search</mat-label>
            <input
              matInput
              [(ngModel)]="searchString"
              (ngModelChange)="onSearchStringChange()"
              />
              @if (searchString) {
                <button
                  mat-icon-button
                  matSuffix
                  (click)="onClearSearch()"
                  aria-label="Clear"
                  >
                  <mat-icon class="clear-icon">close</mat-icon>
                </button>
              }
            </mat-form-field>
            <button color="primary" class="btn btn-primary btn-big search-btn" (click)="onSearch()">
              <mat-icon>search</mat-icon>
              <span>Search</span>
            </button>
            <button mat-flat-button color="secondary"  class="filter-btn" (click)="openAdvancedFiltersPopup()">
              <mat-icon>filter_list</mat-icon>
            </button>
          </div>

          <div class="scroll-x">
            <table mat-table [dataSource]="dataSource" matSort class="custom-table results-table">
              <ng-container matColumnDef="select">
                <th mat-header-cell *matHeaderCellDef>Select simulation</th>
                <td mat-cell *matCellDef="let element">
                  <mat-radio-button
                    [value]="element.simulationId"
                    [disabled]="!isRunResultValid(element)"
                    [checked]="selectedRun?.runId === element.runId"
                    (change)="onPcSimulationSelectionChange(element)">
                  </mat-radio-button>
                </td>
              </ng-container>

              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
                <td mat-cell *matCellDef="let element">{{ element.name }}</td>
              </ng-container>

              <ng-container matColumnDef="fileName">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Rock file</th>
                <td mat-cell *matCellDef="let element">{{ element.fileName | removeExtension }}</td>
              </ng-container>

              <ng-container matColumnDef="direction">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Direction</th>
                <td mat-cell *matCellDef="let element">{{ element.direction | uppercase }}</td>
              </ng-container>

              <ng-container matColumnDef="cycle">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ isUpscaling ? 'Cycles' : 'Cycle' }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (isUpscaling) {
                    {{ getUpscalingCycles(element) }}
                  } @else {
                    {{ getCyclesForRun(element) }}
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="interfactialTension">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ isUpscaling ? 'Interfacial tension interval (N/m)' : 'Interfacial tension (N/m)' }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (isUpscaling) {
                    {{ getUpscalingInterfacialTension(element) }}
                  } @else {
                    {{ element.parameters.interfactialTension }}
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="contactAnglePrimaryDrainage">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ isUpscaling ? 'Primary drainage cosine interval' : 'Primary drainage cosine' }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (isUpscaling) {
                    {{ getUpscalingPrimaryDrainage(element) }}
                  } @else {
                    {{ element.parameters.contactAnglePrimaryDrainage }}
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="contactAngleFirstImbibition">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ isUpscaling ? 'First imbibition cosine interval' : 'First imbibition cosine' }}</th>
                <td mat-cell *matCellDef="let element">
                  @if (isUpscaling) {
                    {{ getUpscalingFirstImbibition(element) }}
                  } @else {
                    {{ element.parameters.contactAngleFirstImbibition ?? '—' }}
                  }
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>

              <tr class="mat-row table-row" *matNoDataRow>
                <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
                  No data matching the filter value
                </td>
              </tr>
            </table>
          </div>
          <mat-paginator
            class="mat-paginator"
            [length]="totalCount"
            [pageIndex]="filterData.page - 1"
            [pageSize]="filterData.pageSize"
            [pageSizeOptions]="[5, 10, 20, 30]"
            aria-label="Select page"
          ></mat-paginator>
        </mat-expansion-panel>

        <mat-expansion-panel [expanded]="true" class="kr-config">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ simulationTitle }} simulation configuration</mat-panel-title>
          </mat-expansion-panel-header>

          @if (selectedSimulation) {
            <div class="kr-input">
              <div class="kr-input-column">
                <div class="chart-container">
                  <highcharts-chart
                    style="width: 100%; height: 550px; display: block; border: 1px solid var(--main_borderColor)"
                    [Highcharts]="Highcharts"
                    [options]="chartOptions"
                    (chartInstance)="saveChartInstance($event)"
                  ></highcharts-chart>
                </div>
              </div>
              <div class="kr-input-column">
                <form [formGroup]="cyclesForm">
                  <div class="input-row">
                    <span>Cycles</span>
                    <div class="direction-options">
                      <mat-checkbox formControlName="d1">PD</mat-checkbox>
                      <mat-checkbox formControlName="imb">FI</mat-checkbox>
                      <mat-checkbox formControlName="d2">SD</mat-checkbox>
                    </div>
                  </div>
                </form>
                <form [formGroup]="simulationNameForm">
                  <div class="input-row">
                    <span class="mb-5">Simulation name</span>
                    <mat-form-field appearance="outline" class="flex">
                      <mat-label>Simulation name</mat-label>
                      <input matInput formControlName="name" />
                      @if (nameControl.hasError('maxlength')) {
                        <mat-error>
                          Simulation name can't contain more than 256 characters.
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                </form>
              </div>
            </div>
          } @else {
            <div class="no-results">
              <mat-icon class="no-results-icon">info</mat-icon>
              <p>Please select Capillary pressure results to configure inputs for {{ simulationTitle }} simulation.</p>
            </div>
          }
        </mat-expansion-panel>
      </div>
      <div class="controls">
        <button
          class="btn btn-primary btn-big submit-btn"
          id="run-btn"
          type="button"
          [disabled]="isLoading || !isSimulationInputValid()"
          (click)="runSimulation()"
          color="primary"
          >
          Run simulation
        </button>
      </div>
    </mat-card-content>
  </mat-card>

